/* eslint-disable no-restricted-globals, no-undef */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  HStack,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Checkbox,
  CheckboxGroup,
  Stack,
  Spinner,
  Icon,
  SimpleGrid,
  useColorModeValue
} from "@chakra-ui/react";
import { RepeatIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaBook, FaClock, FaUsers } from "react-icons/fa";

import axios from "axios";
import { useModalToast } from "../../context/GlobalModalToast";

const ProjectGroups = () => {
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Modals state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const { isOpen: isMembersOpen, onOpen: onMembersOpen, onClose: onMembersClose } = useDisclosure();
  
  // Active selected Group for action modals
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeGroupProgress, setActiveGroupProgress] = useState(null);
  
  // Forms state
  const [newGroupForm, setNewGroupForm] = useState({ name: "", description: "" });
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState([]);
  const [dueDate, setDueDate] = useState("");
  
  const [selectedMemberPairs, setSelectedMemberPairs] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [complianceSearch, setComplianceSearch] = useState("");

  const { toast } = useModalToast();

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [groupsRes, coursesRes, bundlesRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/project-groups`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/courses`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/courses/bundles`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/auth`, config)
      ]);

      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data?.groups || []));
      setCourses(coursesRes.data?.course || coursesRes.data?.courses || (Array.isArray(coursesRes.data) ? coursesRes.data : []));
      setBundles(Array.isArray(bundlesRes.data) ? bundlesRes.data : (bundlesRes.data?.bundles || []));
      // Filter to only display employees/students/users
      const allFetchedUsers = usersRes.data.users || usersRes.data || [];
      setAllUsers(allFetchedUsers);
      
      // If groups are present, set active progress view to first group
      if (groupsRes.data && groupsRes.data.length > 0) {
        setActiveGroupProgress(groupsRes.data[0]);
      }
    } catch (err) {
      console.error("Failed to load project groups core data", err);
      toast({
        title: "Load Error",
        description: "Failed to fetch corporate registry data.",
        status: "error",
        duration: 4000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupForm.name) {
      toast({ title: "Name is required", status: "warning" });
      return;
    }
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/project-groups`, newGroupForm, config);
      toast({ title: "Group Created Successfully", status: "success", duration: 3000 });
      setNewGroupForm({ name: "", description: "" });
      onCreateClose();
      fetchInitialData();
    } catch (err) {
      toast({ title: "Failed to create group", description: err.response?.data?.message, status: "error" });
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to delete this group? All user enrollments and progress logs will be permanently preserved for corporate compliance auditing.")) {
      return;
    }
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/project-groups/${groupId}`, config);
      toast({ title: "Group Deleted Successfully", description: "Audit trail and employee enrollments preserved.", status: "success" });
      fetchInitialData();
    } catch (err) {
      toast({ title: "Failed to delete group", status: "error" });
    }
  };

  const handleOpenMembersModal = (group) => {
    setSelectedGroup(group);
    const pairs = group.members ? group.members.map(m => ({
      userId: m.user?._id || m.user,
      mentorId: m.mentor?._id || m.mentor || ""
    })) : [];
    setSelectedMemberPairs(pairs);
    setSearchUser("");
    onMembersOpen();
  };

  const handleSaveMembers = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/project-groups/${selectedGroup._id}/members`,
        { memberPairs: selectedMemberPairs },
        config
      );
      toast({ title: "Group Roster updated successfully", description: "Enrolled in active courses.", status: "success" });
      onMembersClose();
      fetchInitialData();
    } catch (err) {
      toast({ title: "Failed to update members", status: "error" });
    }
  };

  const handleOpenAssignModal = (group) => {
    setSelectedGroup(group);
    
    // Pre-populate already assigned courses
    const assignedIds = group.assignedCourses ? group.assignedCourses.map(c => typeof c.courseId === 'object' ? c.courseId._id : c.courseId) : [];
    setSelectedCourseIds(assignedIds);
    setSelectedBundleIds([]);
    
    // Pre-populate due date if available on the first assigned course
    const upcomingDueDate = group.assignedCourses && group.assignedCourses.length > 0 && group.assignedCourses[0].dueDate 
      ? new Date(group.assignedCourses[0].dueDate).toISOString().split('T')[0] 
      : "";
    setDueDate(upcomingDueDate);
    
    onAssignOpen();
  };

  const handleAssignCurriculum = async () => {
    if (selectedCourseIds.length === 0 && selectedBundleIds.length === 0) {
      toast({ title: "Please select at least one course or bundle", status: "warning" });
      return;
    }
    setSyncing(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Resolve bundles into course IDs
      let bundleCourseIds = [];
      selectedBundleIds.forEach(bundleId => {
        const bundle = bundles.find(b => b._id === bundleId);
        if (bundle && bundle.courses) {
          bundleCourseIds = [...bundleCourseIds, ...bundle.courses.map(c => c._id || c)];
        }
      });
      
      const allCourseIds = [...new Set([...selectedCourseIds, ...bundleCourseIds])];

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/project-groups/${selectedGroup._id}/courses`,
        { courseIds: allCourseIds, dueDate: dueDate || undefined },
        config
      );

      toast({ 
        title: "Curriculum Assigned Successfully", 
        description: `Bulk enrollments generated for all members.`, 
        status: "success" 
      });
      onAssignClose();
      fetchInitialData();
    } catch (err) {
      toast({ title: "Assignment Failed", description: err.response?.data?.message || "Internal server error", status: "error" });
    } finally {
      setSyncing(false);
    }
  };

  

  

  const employeesList = allUsers.filter(u => 
    (u.role === "employee" || u.role === "student" || u.role === "user" || u.role === "candidate") &&
    (u.fullName?.toLowerCase().includes(searchUser.toLowerCase()) || u.email?.toLowerCase().includes(searchUser.toLowerCase()))
  );

  const mentorsList = allUsers.filter(u => 
    (u.role === "instructor" || u.role === "teacher" || u.role === "admin" || u.role === "super_admin")
  );

  // Compute metrics for active progress group
  const getGroupMetrics = (group) => {
    const total = group?.members?.length || 0;
    if (total === 0) {
      return { total: 0, completed: 0, percentage: 0 };
    }
    if (!group.enrollments || group.enrollments.length === 0) {
      return { total, completed: 0, percentage: 0 };
    }
    
    // Count completed members (a member is completed if all their enrollments are 100% complete)
    let completedCount = 0;
    const totalCourses = group.assignedCourses?.length || 0;
    
    group.members.forEach(member => {
      const memberUser = member.user || member;
      const memberId = String(memberUser?._id || memberUser || '');
      const userEnrolls = (group.enrollments || []).filter(e => String(e.userId) === memberId);
      if (totalCourses > 0 && userEnrolls.length === totalCourses && userEnrolls.every(e => e.status === "Completed" || e.progress === 100)) {
        completedCount++;
      }
    });

    const totalProgress = group.enrollments.reduce((acc, curr) => acc + (curr.progress || 0), 0);
    const avgProgress = Math.round(totalProgress / group.enrollments.length);
    
    return { total, completed: completedCount, percentage: avgProgress };
  };

  const metrics = activeGroupProgress ? getGroupMetrics(activeGroupProgress) : { total: 0, completed: 0, percentage: 0 };

  const bgMain = useColorModeValue("var(--background)", "gray.900");
  const textMain = useColorModeValue("gray.800", "gray.100");
  const textSub = useColorModeValue("gray.500", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.100", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const inputBg = useColorModeValue("white", "gray.700");
  const modalBg = useColorModeValue("white", "gray.800");

  if (loading) {
    return (
    <Flex mx="auto" w="full" justify="center" align="center" minH="100vh" direction="column" gap={4}>
        <Spinner size="xl" color="purple.500" thickness="4px" />
        <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.5px">LOADING ENTERPRISE GROUPS...</Text>
      </Flex>
    );
  }

  return (
    <Box bg={bgMain} minH="100vh" pb={10} color={textMain}>
      <Box pt="90px" px={{ base: 4, md: 8, lg: 10 }}>
        

        <Tabs variant="soft-rounded" colorScheme="purple" mt={4}>
          <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
            <Box>
              <Heading size="lg" fontWeight="900" letterSpacing="-0.5px" color={textMain}>
                👥 Corporate Project Groups
              </Heading>
              <Text fontSize="sm" color={textSub} mt={1} fontWeight="500">
                Deploy and track targeted training curricula across specialized business squads
              </Text>
            </Box>
            
            <TabList bg="white" p={1.5} borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm">
              <Tab fontSize="xs" fontWeight="800" px={5} py={2.5} borderRadius="xl">👥 Manage Groups</Tab>
              <Tab fontSize="xs" fontWeight="800" px={5} py={2.5} borderRadius="xl">📊 Compliance & Progress</Tab>
            </TabList>
          </Flex>

          <TabPanels>
            {/* TAB 1: MANAGE GROUPS */}
            <TabPanel p={0}>
              <Flex justify="flex-end" mb={6}>
                <Button
                  leftIcon={<AddIcon />}
                  bgGradient="linear(to-r, purple.500, blue.500)"
                  color="white"
                  borderRadius="xl"
                  fontWeight="800"
                  fontSize="xs"
                  h="44px"
                  _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
                  boxShadow="md"
                  onClick={onCreateOpen}
                >
                  Create Project Group
                </Button>
              </Flex>

              {groups.length === 0 ? (
                <Flex justify="center" align="center" minH="300px" bg={cardBg} borderRadius="3xl" border="1px dashed" borderColor={cardBorder} p={6}>
                  <VStack spacing={3}>
                    <Icon as={FaUsers} boxSize={10} color="purple.300" />
                    <Text fontWeight="bold" color={textMain}>No Project Groups Established</Text>
                    <Text fontSize="xs" color={textSub}>Establish your first group to bulk-assign courses to employees.</Text>
                  </VStack>
                </Flex>
              ) : (
                <SimpleGrid templateColumns={{ base: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" }} gap={6}>
                  {groups.map((group) => (
                    <Box
                      key={group._id}
                      bg={cardBg}
                      p={6}
                      borderRadius="3xl"
                      border="1px solid"
                      borderColor={cardBorder}
                      boxShadow="sm"
                      transition="transform 0.2s, box-shadow 0.2s"
                      _hover={{ transform: "translateY(-4px)", boxShadow: "md" }}
                      position="relative"
                    >
                      <Flex justify="space-between" align="start" mb={4}>
                        <VStack align="start" spacing={1} maxW="75%">
                          <Heading size="md" fontWeight="850" color={textMain} letterSpacing="-0.5px">
                            {group.name}
                          </Heading>
                          <Text fontSize="11px" color={textSub} fontWeight="bold">
                            Created By: {group.createdBy?.fullName || "HCM System"}
                          </Text>
                        </VStack>
                        <IconButton
                          icon={<DeleteIcon />}
                          variant="ghost"
                          colorScheme="red"
                          size="sm"
                          onClick={() => handleDeleteGroup(group._id)}
                        />
                      </Flex>

                      <Text fontSize="xs" color={textSub} noOfLines={2} minH="36px" mb={5}>
                        {group.description || "No description provided for this business training group."}
                      </Text>

                      <SimpleGrid columns={2} gap={4} py={3} borderTop="1px solid" borderBottom="1px solid" borderColor={cardBorder} mb={6}>
                        <HStack spacing={2}>
                          <Icon as={FaUsers} color="blue.500" />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="10px" color={textSub} fontWeight="bold">MEMBERS</Text>
                            <Text fontWeight="800" fontSize="sm">{group.members?.length || 0} Employees</Text>
                          </VStack>
                        </HStack>

                        <HStack spacing={2}>
                          <Icon as={FaBook} color="purple.500" />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="10px" color={textSub} fontWeight="bold">CURRICULUM</Text>
                            <Text fontWeight="800" fontSize="sm">{group.assignedCourses?.length || 0} Courses</Text>
                          </VStack>
                        </HStack>
                      </SimpleGrid>

                      <Stack direction="row" spacing={3}>
                        <Button
                          flex={1}
                          size="sm"
                          variant="outline"
                          fontSize="11px"
                          fontWeight="800"
                          borderRadius="xl"
                          onClick={() => handleOpenMembersModal(group)}
                        >
                          👥 Members
                        </Button>
                        <Button
                          flex={1}
                          size="sm"
                          bg="purple.50"
                          color="purple.700"
                          _hover={{ bg: "purple.100" }}
                          fontSize="11px"
                          fontWeight="800"
                          borderRadius="xl"
                          onClick={() => handleOpenAssignModal(group)}
                        >
                          📚 Assign Courses
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </TabPanel>

            {/* TAB 2: COMPLIANCE & PROGRESS */}
            <TabPanel p={0}>
              {groups.length === 0 ? (
                <Flex justify="center" align="center" minH="300px" bg={cardBg} borderRadius="3xl" border="1px dashed" borderColor={cardBorder} p={6}>
                  <VStack spacing={3}>
                    <Icon as={FaUsers} boxSize={10} color="purple.300" />
                    <Text fontWeight="bold" color={textMain}>No Training Telemetry Available</Text>
                  </VStack>
                </Flex>
              ) : (
                <VStack spacing={8} align="stretch">
                  <Grid templateColumns={{ base: "1fr", lg: "1fr 2fr" }} gap={8} alignItems="start">
                    <Box bg={cardBg} p={6} borderRadius="3xl" border="1px solid" borderColor={cardBorder} boxShadow="sm">
                      <FormLabel fontSize="xs" fontWeight="bold" color={textSub} mb={2}>SELECT PROJECT GROUP FOR ROSTER</FormLabel>
                      <Select
                        placeholder="Choose training group..."
                        value={activeGroupProgress?._id || ""}
                        onChange={(e) => {
                          const grp = groups.find(g => g._id === e.target.value);
                          if (grp) setActiveGroupProgress(grp);
                        }}
                        borderRadius="xl"
                        fontWeight="700"
                        bg={inputBg}
                      >
                        {groups.map(g => (
                          <option key={g._id} value={g._id}>{g.name}</option>
                        ))}
                      </Select>

                      {activeGroupProgress && (
                        <VStack mt={6} align="start" spacing={4}>
                          <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full">
                            📋 Roster Summary
                          </Badge>
                          <Text fontSize="xs" color={textSub}>
                            {activeGroupProgress.description || "Active corporate training squad for specialized operations and curriculum onboarding."}
                          </Text>
                          <HStack spacing={2} fontSize="xs" fontWeight="bold" color={textSub}>
                            <Icon as={FaClock} />
                            <Text>Sync Status: Live Active Telemetry</Text>
                          </HStack>
                        </VStack>
                      )}
                    </Box>

                    {activeGroupProgress && (
                      <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                        <Box bg={cardBg} p={6} borderRadius="3xl" border="1px solid" borderColor={cardBorder} boxShadow="sm">
                          <VStack align="start" spacing={1}>
                            <Text fontSize="10px" fontWeight="800" color={textSub}>TOTAL GROUP ENROLLMENTS</Text>
                            <Heading size="lg" fontWeight="900" color="blue.500">{metrics.total}</Heading>
                            <Text fontSize="11px" color={textSub}>Enrolled employees</Text>
                          </VStack>
                        </Box>

                        <Box bg={cardBg} p={6} borderRadius="3xl" border="1px solid" borderColor={cardBorder} boxShadow="sm">
                          <VStack align="start" spacing={1}>
                            <Text fontSize="10px" fontWeight="800" color={textSub}>COMPLETED CURRICULA</Text>
                            <Heading size="lg" fontWeight="900" color="green.500">{metrics.completed}</Heading>
                            <Text fontSize="11px" color={textSub}>100% full completions</Text>
                          </VStack>
                        </Box>

                        <Box bg={cardBg} p={6} borderRadius="3xl" border="1px solid" borderColor={cardBorder} boxShadow="sm">
                          <VStack align="start" spacing={1} w="full">
                            <Text fontSize="10px" fontWeight="800" color={textSub}>COMPLIANCE RATIO</Text>
                            <Heading size="lg" fontWeight="900" color="purple.500">{metrics.percentage}%</Heading>
                            <Progress value={metrics.percentage} size="xs" colorScheme="purple" borderRadius="full" w="full" bg="purple.50" />
                          </VStack>
                        </Box>
                      </SimpleGrid>
                    )}
                  </Grid>

                  {activeGroupProgress && (
                    <Box
                      borderRadius="3xl"
                      bg={cardBg}
                      border="1px solid"
                      borderColor={cardBorder}
                      boxShadow="sm"
                      overflow="hidden"
                      p={4}
                    >
                      <Flex justify="space-between" align="center" mb={4} px={2} wrap="wrap" gap={3}>
                        <Box flex={1} minW="250px">
                          <Heading size="sm" fontWeight="850" color={textMain}>
                            👥 {activeGroupProgress.name} Training Compliance Table
                          </Heading>
                          <Text fontSize="11px" color={textSub} mt={1}>
                            Real-time tracking of squad member progress, course syllabus, and designated instructors
                          </Text>
                        </Box>
                        
                        <HStack spacing={3} minW="300px" justify="flex-end">
                          <Input 
                            placeholder="Search employee name or email..."
                            value={complianceSearch}
                            onChange={(e) => setComplianceSearch(e.target.value)}
                            size="xs"
                            borderRadius="lg"
                            w="200px"
                            bg={inputBg}
                          />
                          <Button
                            leftIcon={<RepeatIcon />}
                            size="xs"
                            borderRadius="lg"
                            onClick={fetchInitialData}
                          >
                            Refresh Progress
                          </Button>
                        </HStack>
                      </Flex>

                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead bg={tableHeaderBg}>
                            <Tr>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">EMPLOYEE</Th>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">DEPARTMENT</Th>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">CURRICULUM SYLLABUS</Th>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">COMPLIANCE METRIC</Th>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">DESIGNATED INSTRUCTOR</Th>
                              <Th fontSize="10px" fontWeight="800" color={textSub} letterSpacing="0.5px">STATUS</Th>
                            </Tr>
                          </Thead>
                          
                          <Tbody>
                            {!activeGroupProgress.members || activeGroupProgress.members.length === 0 ? (
                              <Tr>
                                <Td colSpan={6} textAlign="center" py={10}>
                                  <Text fontSize="xs" color={textSub} fontStyle="italic">
                                    No members enrolled in this group. Click 'Members' to add employees.
                                  </Text>
                                </Td>
                              </Tr>
                            ) : (
                              (() => {
                                const filteredMembers = activeGroupProgress.members.filter(member => {
                                  const memberUser = member.user || member;
                                  if (!memberUser) return false;
                                  const name = memberUser.fullName || '';
                                  const email = memberUser.email || '';
                                  const q = complianceSearch.toLowerCase();
                                  return name.toLowerCase().includes(q) || email.toLowerCase().includes(q);
                                });

                                if (filteredMembers.length === 0) {
                                  return (
                                    <Tr>
                                      <Td colSpan={6} textAlign="center" py={10}>
                                        <Text fontSize="xs" color={textSub} fontStyle="italic">
                                          No employees found matching "{complianceSearch}"
                                        </Text>
                                      </Td>
                                    </Tr>
                                  );
                                }

                                return filteredMembers.map((member, idx) => {
                                  const memberUser = member.user || member;
                                  const memberId = String(memberUser?._id || memberUser || '');
                                  const userEnrolls = (activeGroupProgress.enrollments || []).filter(e => String(e.userId) === memberId);
                                  const totalCourses = activeGroupProgress.assignedCourses?.length || 0;
                                  const avgProgress = totalCourses > 0 
                                    ? Math.round(userEnrolls.reduce((sum, e) => sum + (e.progress || 0), 0) / totalCourses)
                                    : 0;
                                  const isCompleted = avgProgress === 100 && userEnrolls.length === totalCourses && totalCourses > 0;
                                  const mentorObj = member.mentor;
                                  
                                  return (
                                  <Tr
                                    key={idx}
                                    transition="all 0.2s"
                                    _hover={{ bg: cardBorder }}
                                  >
                                    <Td borderBottom="1px solid" borderColor={cardBorder}>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="850" color={textMain} fontSize="xs">{memberUser?.fullName}</Text>
                                        <Text fontSize="9px" fontWeight="700" color={textSub}>{memberUser?.email}</Text>
                                      </VStack>
                                    </Td>
                                    <Td borderBottom="1px solid" borderColor={cardBorder}>
                                      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2.5} fontSize="9px" fontWeight="800">
                                        {memberUser?.department || 'Operations'}
                                      </Badge>
                                    </Td>
                                    <Td borderBottom="1px solid" borderColor={cardBorder} minW="250px">
                                      <VStack align="stretch" spacing={3}>
                                        {activeGroupProgress.assignedCourses.map(assigned => {
                                          const course = assigned.courseId;
                                          if (!course) return null;
                                          const enroll = userEnrolls.find(e => String(e.courseId) === String(course._id));
                                          const progress = enroll ? (enroll.progress || 0) : 0;
                                          const isComplete = progress === 100;
                                          return (
                                            <Box key={course._id} w="full">
                                              <Flex justify="space-between" mb={1} fontSize="10px" fontWeight="750" color={textSub}>
                                                <Text>{course.title}</Text>
                                                <Text>{progress}%</Text>
                                              </Flex>
                                              <Progress
                                                value={progress}
                                                size="xs"
                                                colorScheme={isComplete ? "green" : (progress > 0 ? "orange" : "blue")}
                                                borderRadius="full"
                                                bg={cardBorder}
                                              />
                                            </Box>
                                          );
                                        })}
                                        {(!activeGroupProgress.assignedCourses || activeGroupProgress.assignedCourses.length === 0) && (
                                          <Text fontSize="xs" color={textSub}>No courses assigned</Text>
                                        )}
                                      </VStack>
                                    </Td>
                                    <Td minW="150px" borderBottom="1px solid" borderColor={cardBorder}>
                                      <Box w="full">
                                        <Flex justify="space-between" mb={1} fontSize="10px" fontWeight="750" color={textSub}>
                                          <Text>Overall Avg</Text>
                                          <Text>{avgProgress}%</Text>
                                        </Flex>
                                        <Progress
                                          value={avgProgress}
                                          size="xs"
                                          colorScheme={isCompleted ? "green" : "blue"}
                                          borderRadius="full"
                                          bg={cardBorder}
                                        />
                                      </Box>
                                    </Td>
                                    <Td borderBottom="1px solid" borderColor={cardBorder}>
                                      <Badge variant="outline" colorScheme="purple" fontSize="9px" px={2} borderRadius="md" fontWeight="bold">
                                        👨‍🏫 {mentorObj ? (mentorObj.fullName || mentorObj) : 'Not Assigned'}
                                      </Badge>
                                    </Td>
                                    <Td borderBottom="1px solid" borderColor={cardBorder}>
                                      <Badge
                                        colorScheme={isCompleted ? "green" : (avgProgress > 0 ? "orange" : "gray")}
                                        variant="solid"
                                        borderRadius="full"
                                        px={2.5}
                                        py={0.5}
                                        fontSize="8px"
                                        fontWeight="850"
                                      >
                                        {isCompleted ? "Completed" : (avgProgress > 0 ? "In Progress" : "Not Started")}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                )});
                              })()
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* CREATE GROUP MODAL */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" p={2} bg={modalBg} color={textMain}>
          <ModalHeader fontWeight="900" letterSpacing="-0.5px">Create Training Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color={textSub}>Group Name</FormLabel>
                <Input
                  placeholder="e.g. Accounting Compliance Squad"
                  value={newGroupForm.name}
                  onChange={(e) => setNewGroupForm(prev => ({ ...prev, name: e.target.value }))}
                  borderRadius="xl"
                  bg={inputBg}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={textSub}>Description</FormLabel>
                <Textarea
                  placeholder="Provide details about the squad, sectors, or business division..."
                  value={newGroupForm.description}
                  onChange={(e) => setNewGroupForm(prev => ({ ...prev, description: e.target.value }))}
                  borderRadius="xl"
                  rows={3}
                  bg={inputBg}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose} borderRadius="xl">Cancel</Button>
            <Button
              bgGradient="linear(to-r, purple.500, blue.500)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
              onClick={handleCreateGroup}
              borderRadius="xl"
            >
              Establish Group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ASSIGN CURRICULUM MODAL */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" p={2} bg={modalBg} color={textMain}>
          <ModalHeader fontWeight="900" letterSpacing="-0.5px">
            📚 Assign Training to {selectedGroup?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={textSub}>Courses</FormLabel>
                <Box border="1px solid" borderColor={cardBorder} borderRadius="xl" p={3} maxH="200px" overflowY="auto" bg={inputBg}>
                  <CheckboxGroup colorScheme="purple" value={selectedCourseIds} onChange={setSelectedCourseIds}>
                    <Stack spacing={2}>
                      {courses.map(c => (
                        <Checkbox key={c._id} value={c._id}>
                          <Text fontSize="sm">{c.title}</Text>
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={textSub}>Bundles (Multiple)</FormLabel>
                <Select
                  placeholder="Select bundles..."
                  multiple
                  value={selectedBundleIds}
                  onChange={(e) => {
                    const options = Array.from(e.target.selectedOptions).map(o => o.value);
                    setSelectedBundleIds(options);
                  }}
                  borderRadius="xl"
                  height="auto"
                  bg={inputBg}
                >
                  {bundles.map(b => (
                    <option key={b._id} value={b._id}>
                      {b.title} ({b.courses?.length || 0} Courses)
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={textSub}>Completion Due Date (Optional)</FormLabel>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  borderRadius="xl"
                  bg={inputBg}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose} borderRadius="xl" isDisabled={syncing}>Cancel</Button>
            <Button
              bgGradient="linear(to-r, purple.500, blue.500)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
              onClick={handleAssignCurriculum}
              borderRadius="xl"
              isLoading={syncing}
              loadingText="Enrolling..."
            >
              Assign & Enroll
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* MANAGE MEMBERS MODAL */}
      <Modal isOpen={isMembersOpen} onClose={onMembersClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" p={2} maxH="85vh" bg={modalBg} color={textMain}>
          <ModalHeader fontWeight="900" letterSpacing="-0.5px">
            👥 Manage Roster: {selectedGroup?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Search employees or mentors by name..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                borderRadius="xl"
                bg={inputBg}
              />

              <Box border="1px solid" borderColor={cardBorder} borderRadius="xl" p={3} maxH="350px" overflowY="auto" bg={inputBg}>
                {employeesList.length === 0 ? (
                  <Text py={4} align="center" fontSize="xs" color={textSub}>No active employees found matching criteria</Text>
                ) : (
                  <Stack spacing={3}>
                    {employeesList.map((u) => {
                      const pair = selectedMemberPairs.find(p => p.userId === u._id);
                      const isChecked = !!pair;
                      return (
                        <Flex key={u._id} justify="space-between" align="center" p={2} borderRadius="lg" bg={isChecked ? "purple.50" : "transparent"} _hover={{ bg: cardBorder }} transition="background 0.2s" wrap="wrap" gap={2}>
                          <Flex align="center" gap={3}>
                            <Checkbox 
                              colorScheme="purple" 
                              isChecked={isChecked} 
                              onChange={() => {
                                if (isChecked) {
                                  setSelectedMemberPairs(prev => prev.filter(p => p.userId !== u._id));
                                } else {
                                  setSelectedMemberPairs(prev => [...prev, { userId: u._id, mentorId: "" }]);
                                }
                              }} 
                            />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" fontSize="xs">{u.fullName}</Text>
                              <Text fontSize="10px" color={textSub}>{u.email} • {u.department || "Operations"}</Text>
                            </VStack>
                          </Flex>
                          {isChecked && (
                            <Select 
                              size="xs" 
                              w="180px" 
                              borderRadius="md" 
                              bg={cardBg}
                              value={pair.mentorId}
                              onChange={(e) => {
                                setSelectedMemberPairs(prev => prev.map(p => p.userId === u._id ? { ...p, mentorId: e.target.value } : p));
                              }}
                            >
                              <option value="">Unassigned</option>
                              {mentorsList.map(m => (
                                <option key={m._id} value={m._id}>{m.fullName}</option>
                              ))}
                            </Select>
                          )}
                        </Flex>
                      );
                    })}
                  </Stack>
                )}
              </Box>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onMembersClose} borderRadius="xl">Cancel</Button>
            <Button
              bgGradient="linear(to-r, purple.500, blue.500)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, purple.600, blue.600)" }}
              onClick={handleSaveMembers}
              borderRadius="xl"
            >
              Save Members list
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProjectGroups;
